import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { type SequenceStep, SequenceStepStatus, SequenceStepType } from '@/models/sequence'
import { reorderSteps } from './reorder'
import { SequenceStepCard } from './SequenceStepCard'

// Email, SMS, and wait steps carry editable config; call/text are configless.
const EDITABLE_TYPES = [SequenceStepType.SEND_EMAIL, SequenceStepType.SEND_SMS, SequenceStepType.WAIT]

function SortableRow({
  step,
  editable,
  onEdit,
  onDelete,
}: {
  step: SequenceStep
  editable: boolean
  onEdit?: () => void
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: step.id,
    disabled: !editable,
  })
  const style = { transform: CSS.Transform.toString(transform), transition }
  const handle = editable ? (
    <Button
      type="button"
      ref={setNodeRef}
      variant="iconOnly"
      size="icon-sm"
      {...attributes}
      {...listeners}
      aria-label="Drag to reorder"
      className="cursor-grab text-neutral-400"
    >
      <GripVertical className="size-4" />
    </Button>
  ) : (
    <span className="size-4" />
  )
  return (
    <div ref={setNodeRef} style={style}>
      <SequenceStepCard
        step={step}
        editable={editable}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandle={handle}
      />
    </div>
  )
}

interface SequenceStepListProps {
  steps: SequenceStep[]
  reorderable: boolean
  onReorder: (orderedPendingIds: string[]) => void
  onEditStep?: (id: string) => void
  onDeleteStep: (id: string) => void
}

export function SequenceStepList({
  steps,
  reorderable,
  onReorder,
  onEditStep,
  onDeleteStep,
}: SequenceStepListProps) {
  const [items, setItems] = useState(steps)
  useEffect(() => setItems(steps), [steps])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  function handleDragEnd(e: DragEndEvent) {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const { items: next, pendingIds } = reorderSteps(items, String(active.id), String(over.id))
    setItems(next)
    onReorder(pendingIds)
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-2">
          {items.map((step) => (
            <SortableRow
              key={step.id}
              step={step}
              editable={reorderable && step.status === SequenceStepStatus.PENDING}
              onEdit={
                onEditStep && EDITABLE_TYPES.includes(step.stepType) ? () => onEditStep(step.id) : undefined
              }
              onDelete={() => onDeleteStep(step.id)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}
